import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { addDays, format, subDays } from "date-fns";
import { useCoachingAvailabilityPreview } from "@bw/shared/hooks";
import type { CoachingAvailabilityPreviewQuery } from "@bw/shared/api";
import { toZonedCivilDate } from "@/libs/zonedDateTime";

export interface CoachingAvailabilityGridProps {
  requestId: string;
  timezone: string;
  courtId: string;
  durationMinutes: number;
  selectedStart: string | null;
  focusedDate?: string;
  reservationTiming?: "send" | "apply";
  onChoose: (instant: string) => void;
}

const reasonLabel: Record<string, string> = {
  closed: "Facility closed", too_soon: "Too soon to propose",
  coach_busy: "Coach busy", court_busy: "Court busy",
  series_overlap: "Series overlaps",
};

export function CoachingAvailabilityGrid({ requestId, timezone, courtId, durationMinutes,
  selectedStart, focusedDate, reservationTiming = "send", onChoose }: CoachingAvailabilityGridProps) {
  const [day, setDay] = useState(() => toZonedCivilDate(new Date(), timezone));
  const [days, setDays] = useState<1 | 3 | 7>(1);
  const today = toZonedCivilDate(new Date(), timezone);
  const lastStart = addDays(today, 180 - (days - 1));
  useEffect(() => {
    if (!focusedDate || !/^\d{4}-\d{2}-\d{2}$/.test(focusedDate)) return;
    const next = new Date(`${focusedDate}T12:00:00`);
    next.setHours(0, 0, 0, 0);
    const earliest = toZonedCivilDate(new Date(), timezone);
    const latest = addDays(earliest, 180 - (days - 1));
    if (Number.isFinite(next.getTime()) && next >= earliest && next <= latest)
      setDay((current) => format(current, "yyyy-MM-dd") === focusedDate ? current : next);
  }, [focusedDate, timezone, days]);
  const startOn = format(day, "yyyy-MM-dd");
  const query: CoachingAvailabilityPreviewQuery | null = courtId && Number.isSafeInteger(durationMinutes)
    && durationMinutes >= 15 && durationMinutes <= 480
    ? { start_on: startOn, days, duration_minutes: durationMinutes,
        ...(courtId === "none" ? {} : { court_id: courtId }) }
    : null;
  const preview = useCoachingAvailabilityPreview(requestId, query);
  const data = preview.isCurrent && preview.data?.start_on === startOn && preview.data.days === days
    && preview.data.timezone === timezone && preview.data.duration_minutes === durationMinutes
    && preview.data.selected_court_id === (courtId === "none" ? null : courtId)
    ? preview.data : null;
  const mismatched = preview.isCurrent && preview.data && !data;
  const localDays = useMemo(() => Array.from({ length: days }, (_, index) =>
    format(addDays(day, index), "yyyy-MM-dd")), [day, days]);
  const slots = data?.slots ?? [];
  const displayHours = slots.filter((slot) => slot.unavailable_reason !== "closed")
    .map((slot) => Number(slot.local_time.slice(0, 2)));
  const firstHour = displayHours.length ? Math.max(0, Math.min(...displayHours) - 1) : 8;
  const lastHour = displayHours.length ? Math.min(23, Math.max(...displayHours) + 1) : 20;
  const visibleSlots = slots.filter((slot) => {
    const hour = Number(slot.local_time.slice(0, 2));
    return hour >= firstHour && hour <= lastHour;
  });
  const byCell = new Map<string, (typeof visibleSlots)[number]>();
  const occurrences = new Map<string, number>();
  const timeCounts = new Map<string, number>();
  const zonesByTime = new Map<string, Set<string>>();
  for (const slot of [...visibleSlots].sort((left, right) => Date.parse(left.starts_at) - Date.parse(right.starts_at))) {
    const wallTime = slot.local_time.slice(0, 5);
    const base = `${slot.local_day}|${wallTime}`;
    const ordinal = occurrences.get(base) ?? 0;
    occurrences.set(base, ordinal + 1);
    timeCounts.set(wallTime, Math.max(timeCounts.get(wallTime) ?? 0, ordinal + 1));
    const zone = slot.local_time.slice(5).trim();
    if (zone) {
      const zones = zonesByTime.get(wallTime) ?? new Set<string>();
      zones.add(zone);
      zonesByTime.set(wallTime, zones);
    }
    byCell.set(`${base}|${ordinal}`, slot);
  }
  const times = [...timeCounts].flatMap(([wallTime, count]) =>
    Array.from({ length: count }, (_, ordinal) => ({ wallTime, ordinal })))
    .sort((left, right) => left.wallTime.localeCompare(right.wallTime) || left.ordinal - right.ordinal);
  const selectedMs = selectedStart ? Date.parse(selectedStart) : NaN;

  function chooseDate(value: string) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    const next = new Date(`${value}T12:00:00`);
    next.setHours(0, 0, 0, 0);
    if (Number.isFinite(next.getTime()) && next >= today && next <= lastStart) setDay(next);
  }

  return <Box component="section" aria-label="Available coaching times" sx={{ minWidth: 0 }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 1 }}>
      <Typography variant="subtitle2">Available times</Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button size="small" disabled={day <= today} onClick={() => setDay((current) => {
          const previous = subDays(current, days); return previous < today ? today : previous;
        })} aria-label="Previous dates">Previous</Button>
        <Button size="small" onClick={() => setDay(toZonedCivilDate(new Date(), timezone))}>Today</Button>
        <Button size="small" disabled={addDays(day, days) > lastStart}
          onClick={() => setDay(addDays(day, days))} aria-label="Next dates">Next</Button>
        <TextField size="small" type="date" label="Start date" value={startOn}
          onChange={(event) => chooseDate(event.target.value)} slotProps={{ inputLabel: { shrink: true },
            htmlInput: { min: format(today, "yyyy-MM-dd"), max: format(lastStart, "yyyy-MM-dd") } }}
          sx={{ width: 156 }} />
      </Stack>
      <Stack direction="row" spacing={0.5} aria-label="Calendar range">
        {([1, 3, 7] as const).map((count) => <Button key={count} size="small"
          variant={days === count ? "contained" : "outlined"} aria-pressed={days === count}
          onClick={() => { setDays(count); const last = addDays(today, 180 - (count - 1));
            if (day > last) setDay(last); }}>{count === 1 ? "Day" : count === 3 ? "3 days" : "Week"}</Button>)}
      </Stack>
    </Stack>
    {!courtId ? <Typography color="text.secondary" variant="body2">Choose a court or No court required to see times.</Typography>
      : !query ? <Typography color="text.secondary" variant="body2">Enter a duration from 15 to 480 minutes.</Typography>
        : preview.isError || mismatched ? <Alert severity="error">Coaching availability is unavailable. Refresh and try again.</Alert>
          : preview.fetchStatus === "paused" ? <Typography role="status">Waiting for a connection to check availability…</Typography>
          : !data ? <Typography role="status">Loading availability…</Typography>
            : !slots.length ? <Typography color="text.secondary" variant="body2">No available times in this window.</Typography>
              : <>
                {!slots.some((slot) => slot.available) ? <Typography variant="body2" sx={{ mb: 1 }}>
                  No available times in this window. Inspect the calendar or choose another date.
                </Typography> : null}
                <Typography color="text.secondary" variant="caption" sx={{ display: "block", mb: 1 }}>
                  Times in {timezone}. This preview does not reserve a time; {reservationTiming === "apply"
                    ? "applying the accepted reschedule does."
                    : "sending the proposal does."}
                </Typography>
                <Box sx={{ overflowX: "auto", maxHeight: 440, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Box component="table" aria-label="Coaching availability calendar" sx={{ width: "100%", minWidth: days === 7 ? 900 : days === 3 ? 480 : 280,
                    borderCollapse: "collapse", tableLayout: "fixed", "& th, & td": { borderBottom: 1, borderColor: "divider", p: 0.25 },
                    "& thead th": { position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 } }}>
                    <thead><tr><th scope="col" style={{ width: 80 }}>Time</th>{localDays.map((localDay) =>
                      <th key={localDay} scope="col"><Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {format(new Date(`${localDay}T12:00:00`), "EEE, MMM d")}</Typography></th>)}</tr></thead>
                    <tbody>{times.map(({ wallTime, ordinal }) => <tr key={`${wallTime}:${ordinal}`}>
                      <th scope="row"><Typography variant="caption">{wallTime}{ordinal ? ` (${ordinal + 1})` : ""}</Typography></th>
                      {localDays.map((localDay) => {
                        const slot = byCell.get(`${localDay}|${wallTime}|${ordinal}`);
                        if (!slot) return <td key={localDay} />;
                        const selected = Number.isFinite(selectedMs) && Date.parse(slot.starts_at) === selectedMs;
                        const state = slot.available ? slot.within_published_hours ? "Available" : "Available outside published hours"
                          : reasonLabel[slot.unavailable_reason ?? ""] ?? "Unavailable";
                        const zone = slot.local_time.slice(5).trim();
                        const zoneMarker = (zonesByTime.get(wallTime)?.size ?? 0) > 1 && zone ? ` ${zone}` : "";
                        return <td key={localDay}><Button size="small" fullWidth disabled={!slot.available}
                          variant={selected ? "contained" : "text"} aria-pressed={selected}
                          aria-label={`${localDay} ${slot.local_time}${zone ? "" : ordinal ? ` (${ordinal + 1})` : ""}: ${state}`}
                          onClick={() => onChoose(slot.starts_at)}
                          sx={{ minWidth: 0, minHeight: 26, px: 0.5, py: 0.25,
                            border: slot.available && !slot.within_published_hours ? 1 : 0,
                            borderStyle: "dashed", borderColor: "warning.main",
                            bgcolor: selected ? undefined : slot.available ? "action.hover" : undefined }}>
                          {slot.available ? `${selected ? "Selected" : "Open"}${zoneMarker}` : "—"}
                        </Button></td>;
                      })}</tr>)}</tbody>
                  </Box>
                </Box>
              </>}
  </Box>;
}
